import { useCallback, useEffect, useMemo, useState } from "react";
import { useChecklistsQuery } from "@/react/components/entityViews/checklist/api/checklist.api";
import type { ChecklistListProgress } from "@/react/components/entityViews/checklist/api/checklist.types";
import type { Checklist } from "@/react/components/entityViews/checklist/types";
import type { CheckGridOverviewItem, CheckGridRecurrence, CheckGridSummary } from "../types";
import { emptySummary, subjectTypeFromEntityIds } from "../utils/apiAdapters";

function summaryEqual(a: CheckGridSummary, b: CheckGridSummary): boolean {
  return (
    a.subject_count === b.subject_count &&
    a.item_count === b.item_count &&
    a.applicable === b.applicable &&
    a.unresolved === b.unresolved &&
    a.missed === b.missed &&
    a.attention_needed === b.attention_needed &&
    a.last_activity === b.last_activity
  );
}

function readNumericBoardId(boardId: number | string | null): number | null {
  if (boardId == null || boardId === "") return null;
  const n = typeof boardId === "number" ? boardId : Number(boardId);
  return Number.isFinite(n) ? n : null;
}

/** Map shared checklist list row → Checklists view rail / overview card. */
function overviewFromChecklist(checklist: Checklist): CheckGridOverviewItem {
  const task_id = checklist.taskId != null ? Number(checklist.taskId) : null;
  const client_id = checklist.clientId != null ? Number(checklist.clientId) : null;
  const resource_id = checklist.resourceId != null ? Number(checklist.resourceId) : null;
  const totalItems = checklist.totalItems ?? 0;
  const left = checklist.itemsLeft ?? 0;
  const assignees = checklist.assignedBoardUsers ?? [];

  return {
    id: Number(checklist.id),
    name: checklist.title,
    subject_type: subjectTypeFromEntityIds({ task_id, client_id, resource_id }),
    recurrence: (checklist.recurrence as CheckGridRecurrence) || "none",
    recurrence_weekdays: checklist.recurrenceWeekdays ?? undefined,
    archived: Boolean(checklist.archived),
    can_view_grant: checklist.canView,
    can_edit_grant: checklist.canEdit,
    created_at: checklist.createdAt != null ? String(checklist.createdAt) : undefined,
    task_id,
    client_id,
    resource_id,
    assigned_boarduser_ids: assignees,
    entity_name: checklist.entityTitle ?? null,
    summary: emptySummary({
      subject_count: assignees.length,
      item_count: totalItems,
      applicable: totalItems,
      unresolved: left
    })
  };
}

export type CheckgridsOverviewListFilters = {
  assignedTo?: number | null;
  filterEntityType?: "task" | "resource" | "client" | null;
  createdWithinDays?: 7 | 30 | 90 | null;
  search?: string | null;
  recurrence?: string | null;
};

const OVERVIEW_PROGRESS: ChecklistListProgress[] = ["in_progress", "completed"];

/** Paged rail list — caller loads more on scroll (do not drain pages here). */
function useRailBoardChecklists(
  boardId: number | null,
  archived: boolean,
  listFilters: CheckgridsOverviewListFilters
) {
  return useChecklistsQuery({
    boardId,
    entityType: null,
    entityId: null,
    archive: archived,
    assignedTo: listFilters.assignedTo ?? null,
    filterEntityType: listFilters.filterEntityType ?? null,
    createdWithinDays: listFilters.createdWithinDays ?? null,
    search: listFilters.search ?? null,
    recurrence: listFilters.recurrence ?? null,
    enabled: boardId != null
  });
}

/**
 * Right now overview: progress only (`in_progress` / `completed`), no HQ list filters.
 * Unpaged; if the server still paginates, finish loading so lane "View more" sees the full set.
 */
function useProgressBoardChecklists(boardId: number | null, archived: boolean) {
  const query = useChecklistsQuery({
    boardId,
    entityType: null,
    entityId: null,
    archive: archived,
    progress: OVERVIEW_PROGRESS,
    enabled: boardId != null
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    ...query,
    isLoading: query.isLoading || Boolean(hasNextPage) || isFetchingNextPage
  };
}

/**
 * Board-wide checklist list for the Checklists view rail + Right now overview.
 * Overview is progress-only; rail is scroll-paged and accepts HQ list filters.
 */
export function useCheckgridsOverview(
  boardId: number | string | null,
  archived = false,
  listFilters: CheckgridsOverviewListFilters = {}
) {
  const numericBoardId = readNumericBoardId(boardId);
  const filters: CheckgridsOverviewListFilters = {
    assignedTo: listFilters.assignedTo ?? null,
    filterEntityType: listFilters.filterEntityType ?? null,
    createdWithinDays: listFilters.createdWithinDays ?? null,
    search: listFilters.search ?? null,
    recurrence: listFilters.recurrence ?? null
  };

  const all = useRailBoardChecklists(numericBoardId, archived, filters);
  const inProgress = useProgressBoardChecklists(numericBoardId, archived);
  const { refetch, error: queryError } = all;
  const { refetch: refetchProgress } = inProgress;

  const [overlays, setOverlays] = useState<Record<number, Partial<CheckGridOverviewItem>>>({});

  const withOverlays = useCallback(
    (checklists: Checklist[]) =>
      checklists.map((checklist) => {
        const base = overviewFromChecklist(checklist);
        const overlay = overlays[base.id];
        if (!overlay) return base;
        return {
          ...base,
          ...overlay,
          summary: overlay.summary ?? base.summary
        };
      }),
    [overlays]
  );

  const grids = useMemo(() => withOverlays(all.checklists), [withOverlays, all.checklists]);
  const progressGrids = useMemo(() => withOverlays(inProgress.checklists), [withOverlays, inProgress.checklists]);

  const refresh = useCallback(async () => {
    await Promise.all([refetch(), refetchProgress()]);
  }, [refetch, refetchProgress]);

  const patchSummary = useCallback((id: number, summary: CheckGridSummary) => {
    setOverlays((prev) => {
      const current = prev[id]?.summary;
      if (current && summaryEqual(current, summary)) return prev;
      return { ...prev, [id]: { ...prev[id], summary } };
    });
  }, []);

  const patchOverview = useCallback((id: number, patch: Partial<CheckGridOverviewItem>) => {
    setOverlays((prev) => {
      const current = prev[id] ?? {};
      const next = { ...current, ...patch, summary: patch.summary ?? current.summary };
      const sameAssignees =
        (next.assigned_boarduser_ids?.length ?? 0) === (current.assigned_boarduser_ids?.length ?? 0) &&
        (next.assigned_boarduser_ids ?? []).every((assigneeId, i) => assigneeId === current.assigned_boarduser_ids?.[i]);
      if (
        next.name === current.name &&
        next.subject_type === current.subject_type &&
        next.recurrence === current.recurrence &&
        next.archived === current.archived &&
        next.can_edit_grant === current.can_edit_grant &&
        next.can_view_grant === current.can_view_grant &&
        next.task_id === current.task_id &&
        next.client_id === current.client_id &&
        next.resource_id === current.resource_id &&
        sameAssignees &&
        (patch.summary == null || (current.summary != null && summaryEqual(current.summary, patch.summary)))
      ) {
        return prev;
      }
      return { ...prev, [id]: next };
    });
  }, []);

  return {
    grids,
    progressGrids,
    totalCount: all.totalCount,
    isLoading: all.isLoading,
    isProgressLoading: inProgress.isLoading,
    isFetchingNextPage: all.isFetchingNextPage,
    hasNextPage: all.hasNextPage,
    fetchNextPage: all.fetchNextPage,
    error: queryError?.message ?? null,
    progressError: inProgress.error?.message ?? null,
    refresh,
    patchSummary,
    patchOverview
  };
}
